import type { Api } from '@limuzin/kit';
import { enrolPush, getConfig } from './api';

/**
 * Web Push: what reaches the passenger with no tab of the app in front of them — the
 * driver writing, «Поддержка» answering, a предзаказ's car setting off (ADR 0027).
 *
 * The browser subscribes to its own vendor's push service — Google's, Apple's,
 * Mozilla's — and the server sends there, encrypted to this browser, so the vendor
 * carries it without reading it. With a tab in front of somebody the socket says it
 * instead, and the server skips this browser: the tab tells the server which browser
 * it is, and whether it is on screen, through `Realtime.present`.
 *
 * Everything here is quiet about failing. A browser with no push at all — an iPhone's
 * Safari outside the home screen, a private window — is where the web app always was:
 * told with a tab open, and the dot on coming back.
 */

const pushable = () =>
  typeof Notification !== 'undefined' &&
  'serviceWorker' in navigator &&
  typeof PushManager !== 'undefined';

/**
 * Subscribes this browser, if the passenger has allowed notifications, and tells the
 * server. Resolves with the endpoint the server now knows the browser by, or null
 * when it cannot be reached with no tab open.
 *
 * Every time the app starts, not once: the server forgets a browser when every
 * session is revoked, and a browser may drop its subscription on its own.
 */
export async function enrol(api: Api): Promise<string | null> {
  if (!pushable() || Notification.permission !== 'granted') return null;
  try {
    // A registered worker only. There is none in development, and `ready` would wait
    // for one for ever.
    if (!(await navigator.serviceWorker.getRegistration())) return null;
    const worker = await navigator.serviceWorker.ready;

    const { pushKey } = await getConfig(api);
    if (!pushKey) return null;
    const key = fromBase64Url(pushKey);

    let subscription = await worker.pushManager.getSubscription();
    // Made against a key the server no longer holds: nothing it sends would arrive.
    if (subscription && !sameKey(subscription.options.applicationServerKey, key)) {
      await subscription.unsubscribe();
      subscription = null;
    }
    subscription ??= await worker.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: key,
    });

    const { endpoint, keys } = subscription.toJSON();
    if (!endpoint || !keys?.p256dh || !keys.auth) return null;
    await enrolPush(api, { endpoint, keys: { p256dh: keys.p256dh, auth: keys.auth } });
    return endpoint;
  } catch {
    return null;
  }
}

/**
 * Signing out: this browser stops being reachable, so whoever signs in next is not
 * told the last passenger's messages. The push service then answers the server
 * «gone», which removes the browser there too. Nothing waits on it.
 */
export function leave(): void {
  if (!pushable()) return;
  void navigator.serviceWorker
    .getRegistration()
    .then((registration) => registration?.pushManager.getSubscription())
    .then((subscription) => subscription?.unsubscribe())
    .catch(() => {});
}

function fromBase64Url(text: string): Uint8Array<ArrayBuffer> {
  const base64 = text.replace(/-/g, '+').replace(/_/g, '/');
  const binary = atob(base64 + '='.repeat((4 - (base64.length % 4)) % 4));
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i);
  return bytes;
}

function sameKey(held: ArrayBuffer | null, key: Uint8Array): boolean {
  if (!held || held.byteLength !== key.length) return false;
  const bytes = new Uint8Array(held);
  return bytes.every((byte, i) => byte === key[i]);
}
